import React from 'react';
import { Consignment } from '@/data/mockData';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Download, MoreVertical, GripVertical } from 'lucide-react';
import { useDraggable } from '@dnd-kit/core';
import { CSS } from '@dnd-kit/utilities';
import { cn } from '@/lib/utils';
import { useTranslation } from 'react-i18next';
import { HoverCard, HoverCardContent, HoverCardTrigger } from '@/components/ui/hover-card';

interface ConvoyGroupProps {
  shipment: string;
  items: Consignment[];
  selectedIds: string[];
  onToggleSelection: (id: string) => void;
  onImport: (id: string) => void;
  tableId: string;
  columns: { id: string; visible: boolean }[];
}

const ConvoyGroup: React.FC<ConvoyGroupProps> = ({
  shipment,
  items,
  selectedIds,
  onToggleSelection,
  onImport,
  tableId,
  columns
}) => {
  const { t } = useTranslation();
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id: `convoy-${tableId}-${shipment}`,
    data: {
      type: 'convoy',
      items: items
    }
  });

  const style = {
    transform: CSS.Translate.toString(transform),
    opacity: isDragging ? 0.5 : 1,
    cursor: isDragging ? 'grabbing' : 'grab',
    zIndex: isDragging ? 1000 : 'auto',
    position: isDragging ? ('relative' as const) : undefined
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={cn(
        'border rounded-lg mb-4 transition-all',
        isDragging ? 'border-primary bg-accent/20 shadow-lg' : 'border-transparent hover:border-primary/20'
      )}>
      <div
        className={cn(
          'p-2 rounded-lg',
          // Add a dashed border container if needed, or just style the group
          'border-2 border-dashed border-transparent hover:border-primary/50 transition-colors'
        )}>
        {/* Convoy Header / Group Indicator */}
        <div className="flex items-center gap-2 mb-2 px-2">
          <div
            {...listeners}
            {...attributes}
            className="cursor-grab active:cursor-grabbing text-muted-foreground hover:text-foreground p-1"
            data-testid="drag-handle">
            <GripVertical className="w-5 h-5" />
          </div>
          <h4 className="font-semibold text-lg">{shipment}</h4>
        </div>

        <div className="bg-card rounded-md border border-border overflow-hidden">
          <Table className="table-fixed">
            <TableBody>
              {items.map((row) => (
                <TableRow key={row.id} className="hover:bg-muted/50">
                  <TableCell className="font-medium w-[5%]">
                    {/* Empty or specific content for the first column in the group */}
                  </TableCell>
                  {columns.map((col) => {
                    if (!col.visible) return null;
                    switch (col.id) {
                      case 'vehicle':
                        return (
                          <TableCell key={col.id} className="w-[8%] truncate" title={row.vehicle}>
                            {row.vehicle}
                          </TableCell>
                        );
                      case 'arrivingTo':
                        return (
                          <TableCell key={col.id} className="w-[32%] truncate" title={row.arrivingTo}>
                            {row.arrivingTo}
                          </TableCell>
                        );
                      case 'consignmentNumber':
                        return (
                          <TableCell key={col.id} className="w-[15%]">
                            <HoverCard openDelay={200} closeDelay={100}>
                              <HoverCardTrigger asChild>
                                <Badge
                                  variant="outline"
                                  className="font-semibold cursor-pointer"
                                  data-testid={`consignment-badge-${row.consignmentNumber}`}>
                                  {row.consignmentNumber}
                                </Badge>
                              </HoverCardTrigger>
                              <HoverCardContent className="w-80 p-0">
                                {row.details ? (
                                  <div className="flex flex-col gap-2 p-4">
                                    <h4 className="font-semibold text-sm">{row.details.title}</h4>
                                    <div className="grid grid-cols-2 gap-2 text-xs">
                                      <div className="text-muted-foreground">{t('hover.sender')}</div>
                                      <div className="text-right font-medium">
                                        {row.details.senderName}, {row.details.senderOrganization}
                                      </div>

                                      <div className="text-muted-foreground">{t('hover.receiver')}</div>
                                      <div className="text-right font-medium">
                                        {row.details.receiverName}, {row.details.receiverOrganization}
                                      </div>
                                    </div>

                                    <div className="grid grid-cols-2 gap-1 text-xs mt-2 border-t pt-2">
                                      <div className="text-muted-foreground">{t('hover.weight')}</div>
                                      <div className="text-right font-medium">{row.details.weight}</div>
                                      <div className="text-muted-foreground">{t('hover.volume')}</div>
                                      <div className="text-right font-medium">{row.details.volume}</div>
                                    </div>

                                    <div className="space-y-1 text-xs mt-2 border-t pt-2">
                                      <div className="flex justify-between">
                                        <div className="flex items-center gap-1">
                                          <div className="w-2 h-2 rounded-full bg-red-500"></div>
                                          <span className="text-muted-foreground">{t('hover.remainsWithSender')}</span>
                                        </div>
                                        <span className="font-medium">{row.details.remainsWithSender}</span>
                                      </div>
                                      <div className="flex justify-between pl-3">
                                        <span className="text-muted-foreground">{t('hover.cancelledBySender')}</span>
                                        <span className="font-medium">{row.details.cancelledBySender}</span>
                                      </div>
                                      <div className="flex justify-between pl-3">
                                        <span className="text-muted-foreground">{t('hover.receivedFromSender')}</span>
                                        <span className="font-medium">{row.details.receivedFromSender.toFixed(3)}</span>
                                      </div>
                                      <div className="flex justify-between pl-3">
                                        <span className="text-muted-foreground">{t('hover.transferOut')}</span>
                                        <span className="font-medium">{row.details.transferOut.toFixed(3)}</span>
                                      </div>
                                      <div className="flex justify-between pl-3">
                                        <span className="text-muted-foreground">{t('hover.transferIn')}</span>
                                        <span className="font-medium">{row.details.transferIn}</span>
                                      </div>
                                      <div className="flex justify-between mt-1">
                                        <div className="flex items-center gap-1">
                                          <div className="w-2 h-2 rounded-full bg-green-500"></div>
                                          <span className="text-muted-foreground">{t('hover.stockOnHand')}</span>
                                        </div>
                                        <span className="font-medium">{row.details.stockOnHand.toFixed(3)}</span>
                                      </div>
                                      <div className="flex justify-between pl-3">
                                        <span className="text-muted-foreground">{t('hover.reportedLost')}</span>
                                        <span className="font-medium">{row.details.reportedLost}</span>
                                      </div>
                                      <div className="flex justify-between pl-3">
                                        <span className="text-muted-foreground">{t('hover.releasedToReceiver')}</span>
                                        <span className="font-medium">{row.details.releasedToReceiver}</span>
                                      </div>
                                    </div>

                                    <div className="flex justify-between text-xs font-semibold border-t pt-2 mt-1">
                                      <span>{t('hover.totalPerSrf')}</span>
                                      <span>{row.details.totalPerSrf.toFixed(3)}</span>
                                    </div>
                                  </div>
                                ) : (
                                  <div className="p-4 text-sm text-muted-foreground text-center">
                                    {t('hover.noDetails')}
                                  </div>
                                )}
                              </HoverCardContent>
                            </HoverCard>
                          </TableCell>
                        );
                      case 'dispatched':
                        return (
                          <TableCell key={col.id} className="w-[10%] truncate" title={row.dispatched}>
                            {row.dispatched}
                          </TableCell>
                        );
                      case 'waybill':
                        return (
                          <TableCell key={col.id} className="w-[10%] truncate" title={row.waybill}>
                            {row.waybill}
                          </TableCell>
                        );
                      case 'sender':
                        return (
                          <TableCell key={col.id} className="w-[8%] truncate" title={row.sender}>
                            {row.sender}
                          </TableCell>
                        );
                      default:
                        return null;
                    }
                  })}
                  <TableCell className="w-[8%]">
                    <div className="flex items-center gap-1">
                      <Button
                        variant="outline"
                        size="icon"
                        className="h-9 w-9"
                        onClick={() => onImport(row.id)}
                        aria-label="Import">
                        <Download className="w-4 h-4" />
                      </Button>
                      <Button variant="ghost" size="icon" className="h-9 w-9">
                        <MoreVertical className="w-4 h-4" />
                      </Button>
                    </div>
                  </TableCell>
                  <TableCell className="w-[4%]">
                    <Checkbox
                      checked={selectedIds.includes(row.id)}
                      onCheckedChange={() => onToggleSelection(row.id)}
                      aria-label={`Select ${row.consignmentNumber}`}
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </div>
    </div>
  );
};

export default ConvoyGroup;
